import type { OpportunityRelatedSurchargePeriod } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { type TravelWindow, staleRangeReason } from '../propertySetup/propertySetupModel';

import { parseIsoDateUtc } from './computeNightlySurcharge';
import { WEEKDAYS } from './surchargeGridsModel';
import type { SurchargePeriodFormValues } from './types';

export type SurchargeDates = { startDate: string; endDate: string; skipped: boolean };

export type PreviousSurcharge = { key: string; period: SurchargePeriodFormValues; accumulates: boolean };

export type PreviousSurcharges = {
  periods: Array<PreviousSurcharge>;
  toDate: Array<PreviousSurcharge>;
  noSell: number;
  paidToHotel: number;
  otherCurrencyCost: number;
  outsideTravel: number;
  ended: number;
};

const PAID_TO_HOTEL_LABEL = 'paid directly to the hotel';
const EVERY_DAY_LABEL = 'all';
const NOT_SPECIFIC_DAYS_LABEL = 'no';
const NOT_ACCUMULATING_LABEL = 'no';

function isLabel(value: string | null | undefined, label: string) {
  return value?.trim().toLowerCase() === label;
}

function weekdaysOf({ applies_to_specific_days__c, day_of_the_week__c }: OpportunityRelatedSurchargePeriod) {
  if (isLabel(applies_to_specific_days__c, NOT_SPECIFIC_DAYS_LABEL) || isLabel(day_of_the_week__c, EVERY_DAY_LABEL)) {
    return [];
  }
  const days = (day_of_the_week__c ?? '').split(';').map((day) => day.trim().slice(0, 3).toLowerCase());
  return WEEKDAYS.filter((weekday) => days.includes(weekday));
}

function costOf(value: number | string | null | undefined): number | null {
  const cost = value == null || value === '' ? NaN : Number(value);
  return Number.isFinite(cost) ? cost : null;
}

function overlaps(a: SurchargePeriodFormValues, b: SurchargePeriodFormValues) {
  return a.startDate <= b.endDate && b.startDate <= a.endDate;
}

export function isValidSurchargeRange({ startDate, endDate }: Pick<SurchargeDates, 'startDate' | 'endDate'>) {
  return parseIsoDateUtc(startDate) != null && parseIsoDateUtc(endDate) != null && startDate <= endDate;
}

export function isSurchargeDatesDone(dates: SurchargeDates) {
  return dates.skipped || isValidSurchargeRange(dates);
}

export function reducePreviousSurcharges(
  surchargePeriods: Array<OpportunityRelatedSurchargePeriod> = [],
  currency: string,
  travelWindow: TravelWindow,
  today: string,
): PreviousSurcharges {
  const { travelFrom, travelTo } = travelWindow;
  const result: PreviousSurcharges = {
    periods: [],
    toDate: [],
    noSell: 0,
    paidToHotel: 0,
    otherCurrencyCost: 0,
    outsideTravel: 0,
    ended: 0,
  };
  for (const surchargePeriod of surchargePeriods) {
    if (isLabel(surchargePeriod.collected_by__c, PAID_TO_HOTEL_LABEL)) {
      result.paidToHotel += 1;
      continue;
    }

    const sellAud = Number(surchargePeriod.price__c) || 0;
    if (sellAud <= 0) {
      result.noSell += 1;
      continue;
    }

    const staleReason =
      surchargePeriod.from__c && surchargePeriod.to__c
        ? staleRangeReason({ startDate: surchargePeriod.from__c, endDate: surchargePeriod.to__c }, travelWindow, today)
        : undefined;
    if (staleReason) {
      result[staleReason] += 1;
      continue;
    }

    const cost = costOf(surchargePeriod.cost__c);
    const costInCurrency = surchargePeriod.currencyisocode === currency;
    if (cost !== null && !costInCurrency) {
      result.otherCurrencyCost += 1;
    }

    const period = {
      name: surchargePeriod.name?.trim() ?? '',
      startDate: surchargePeriod.from__c || travelFrom || '',
      endDate: surchargePeriod.to__c || travelTo || '',
      sellAud,
      costLocal: costInCurrency ? cost : null,
      weekdays: weekdaysOf(surchargePeriod),
    };
    const surcharge = {
      key: surchargePeriod.id,
      period,
      accumulates: !isLabel(surchargePeriod.do_these_surcharges_accumulate__c, NOT_ACCUMULATING_LABEL),
    };
    if (surchargePeriod.from__c && surchargePeriod.to__c) {
      result.periods.push(surcharge);
    } else {
      result.toDate.push(surcharge);
    }
  }

  return result;
}

export function chosenSurchargeDates(
  toDate: Array<PreviousSurcharge>,
  picked: Record<string, Partial<SurchargeDates>>,
): Record<string, SurchargeDates> {
  return Object.fromEntries(
    toDate.map(({ key, period }) => [
      key,
      {
        startDate: picked[key]?.startDate ?? period.startDate,
        endDate: picked[key]?.endDate ?? period.endDate,
        skipped: picked[key]?.skipped ?? false,
      },
    ]),
  );
}

export function withChosenSurchargeDates(
  { periods, toDate }: PreviousSurcharges,
  datesByKey: Record<string, SurchargeDates>,
): Array<PreviousSurcharge> {
  const dated = toDate
    .filter(({ key }) => !datesByKey[key].skipped)
    .map((surcharge) => {
      const { startDate, endDate } = datesByKey[surcharge.key];
      return { ...surcharge, period: { ...surcharge.period, startDate, endDate } };
    });
  return [...periods, ...dated];
}

export function countOverlappingNotAccumulating(surcharges: Array<PreviousSurcharge>): number {
  const dated = surcharges.filter(({ period }) => isValidSurchargeRange(period));
  return dated.filter(
    (surcharge) =>
      !surcharge.accumulates && dated.some((other) => other !== surcharge && overlaps(surcharge.period, other.period)),
  ).length;
}
